import { Modal, Button } from "antd";
import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useReactToPrint } from "react-to-print";
import QRCode from "qrcode";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import checkPosType from "../../utils/posType";
import getPakistanDateTime from "../../utils/dateTimeUtils";
import { INVOICE_TYPE_POS } from "../../utils/constants";

const setRefs = (el, ...refs) => {
  refs.forEach((r) => {
    if (r) r.current = el;
  });
};

const PrintInvoice = ({ isModalOpen, setIsModalOpen, printData, isEstimate = false, autoPrint = false, renderContentOnly = false, contentRef = null }) => {
  const compnentRef = useRef();
  const mergeRef = (el) => setRefs(el, compnentRef, contentRef);
  const [qrImage, setQrImage] = useState("");
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const location = useLocation();
  const hasPrintedRef = useRef(false);
  const printTimeoutRef = useRef(null);
  const isPrintingRef = useRef(false);
  const user = useSelector((state) => state.user);
  console.log("locatoin", location);
  console.log("user", user);

  const businessName = user?.businessName || "";
  const licenceNumber = (user?.licenceNumber || "").trim();
  const posType = checkPosType(businessName);
  // console.log()
  console.log("posType", posType);
  console.log("user", user);

  const roundFigure = (value) => {
    if (value === null || value === undefined) return 0;

    // Handle Mongo Decimal / objects
    if (typeof value === "object" && value.$numberDecimal) {
      return Math.round(Number(value.$numberDecimal));
    }

    const num = Number(String(value).replace(/,/g, ""));
    return isNaN(num) ? 0 : Math.round(num);
  };

  const safeToFixed = (value, decimals = 2) => {
    if (value === null || value === undefined) return "0.00";

    // Handle Mongo Decimal / objects
    if (typeof value === "object" && value.$numberDecimal) {
      return Number(value.$numberDecimal).toFixed(decimals);
    }

    const num = Number(String(value).replace(/,/g, ""));
    return isNaN(num) ? "0.00" : num.toFixed(decimals);
  };

  // Format time like "6:43PM" from a "hh:mm:ss AM/PM" string
  const formatShortTime = (timeStr) => {
    if (!timeStr) return "-";
    const parts = timeStr.split(":");
    if (parts.length < 3) {
      return timeStr.replace(/\s/g, "");
    }
    const [hour, minute, rest] = parts;
    const period = rest.split(" ")[1] || "";
    return `${hour}:${minute} ${period}`;
  };

  // FBR format: NTN 7 digits, STRN 13 digits — pad with leading zeros when value came as number
  const formatNtn = (value) => {
    if (value == null || value === "") return "";
    const s = String(value).replace(/\D/g, "");
    return s.length > 0 ? s.padStart(7, "0") : "";
  };
  const formatStrn = (value) => {
    if (value == null || value === "") return "";
    const s = String(value).replace(/\D/g, "");
    return s.length > 0 ? s.padStart(13, "0") : "";
  };

  // Digital invoice: sales tax % from value excl. tax and tax amount (e.g. 18%)
  const getSalesTaxPercent = (item) => {
    const valueExcl = (Number(item.quantity) || 0) * (Number(item.price) || 0);
    if (!valueExcl) return null;
    const tax = Number(item.tax) || 0;
    return (tax / valueExcl) * 100;
  };

  const formatTaxPercent = (item) => {
    const pct = getSalesTaxPercent(item);
    return pct != null ? `${Number(pct.toFixed(0))}%` : "—";
  };

  const isPosInvoice = user?.invoiceType === INVOICE_TYPE_POS;

  // POS: show Disc column only when at least one item has discount
  const hasAnyItemDiscount = Boolean(
    isPosInvoice &&
    Array.isArray(printData?.cartItems) &&
    printData.cartItems.some((item) => (Number(item.discount) || 0) > 0)
  );

  // POS: per-item discount amount from percentage
  const getItemDiscountAmount = (item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const pct = Number(item.discount) || 0;
    return (qty * price * pct) / 100;
  };

  // POS: gross subtotal (before any discount) and total discount % for print
  const posDiscountSummary = useMemo(() => {
    if (!isPosInvoice || !Array.isArray(printData?.cartItems)) {
      return { grossSubtotal: 0, totalDiscountPercent: 0 };
    }
    const gross = printData.cartItems.reduce(
      (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.price) || 0),
      0
    );
    const discountAmount = Number(printData?.discount) || 0;
    const pct = gross > 0 ? (discountAmount / gross) * 100 : 0;
    return { grossSubtotal: gross, totalDiscountPercent: pct };
  }, [isPosInvoice, printData?.cartItems, printData?.discount]);

  // Enhanced print handler with error handling and printer configuration
  const handlePrint = useReactToPrint({
    content: () => compnentRef.current,
    onBeforeGetContent: () => {
      // Ensure all images are loaded before printing
      return new Promise((resolve) => {
        if (isEstimate || !qrImage) {
          resolve();
          return;
        }
        // Wait for images to be fully loaded
        const checkImages = () => {
          const images = compnentRef.current?.querySelectorAll('img');
          if (!images || images.length === 0) {
            resolve();
            return;
          }
          let loadedCount = 0;
          const totalImages = images.length;
          images.forEach((img) => {
            if (img.complete) {
              loadedCount++;
            } else {
              img.onload = () => {
                loadedCount++;
                if (loadedCount === totalImages) resolve();
              };
              img.onerror = () => {
                loadedCount++;
                if (loadedCount === totalImages) resolve();
              };
            }
          });
          if (loadedCount === totalImages) resolve();
        };
        setTimeout(checkImages, 500);
      });
    },
    onPrintError: (error) => {
      console.error("Print error:", error);
      isPrintingRef.current = false;
      // Optionally show user-friendly error message
    },
    pageStyle: `
      @page {
        size: auto;
        margin: 0;
      }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color: #000 !important;
        }
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color: #000 !important;
        }
      }
    `,
    documentTitle: `Invoice-${printData?.invoiceNumber || 'Unknown'}`,
  });

  // Safe print wrapper with debouncing and error handling
  const safePrint = useCallback(() => {
    if (isPrintingRef.current) {
      console.log("Print already in progress, skipping...");
      return;
    }

    if (!compnentRef.current) {
      console.error("Print content ref not available");
      return;
    }

    isPrintingRef.current = true;
    setIsPrinting(true);
    hasPrintedRef.current = true;

    try {
      // Clear any existing timeout
      if (printTimeoutRef.current) {
        clearTimeout(printTimeoutRef.current);
      }

      // Increased delay for Windows 8 and slower printers
      printTimeoutRef.current = setTimeout(() => {
        try {
          const printResult = handlePrint();

          // handlePrint() may return undefined or a Promise
          if (printResult && typeof printResult.then === 'function') {
            // It's a Promise
            printResult
              .then(() => {
                isPrintingRef.current = false;
                setIsPrinting(false);
              })
              .catch((error) => {
                console.error("Print execution error:", error);
                isPrintingRef.current = false;
                setIsPrinting(false);
              });
          } else {
            // It's not a Promise, just reset state after a short delay
            setTimeout(() => {
              isPrintingRef.current = false;
              setIsPrinting(false);
            }, 500);
          }
        } catch (error) {
          console.error("Print execution error:", error);
          isPrintingRef.current = false;
          setIsPrinting(false);
        }
      }, isEstimate ? 500 : 1000); // Longer delay for non-estimate (QR code)
    } catch (error) {
      console.error("Print setup error:", error);
      isPrintingRef.current = false;
      setIsPrinting(false);
    }
  }, [handlePrint, isEstimate]);

  // Reset print state when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      hasPrintedRef.current = false;
      isPrintingRef.current = false;
      setIsPrinting(false);
      setImagesLoaded(false);
      // Clear any pending print timeouts
      if (printTimeoutRef.current) {
        clearTimeout(printTimeoutRef.current);
        printTimeoutRef.current = null;
      }
    }
  }, [isModalOpen]);

  // Generate QR code (only if FBR invoice number exists)
  useEffect(() => {
    const generateQR = async () => {
      if (isEstimate) {
        setQrImage("");
        setImagesLoaded(true);
        return; // estimate invoices do not require QR / FBR ref
      }
      // Only generate QR if fbrInvoiceNumber exists and is not null/empty
      if (!printData?.fbrInvoiceNumber || printData.fbrInvoiceNumber === "0" || printData.fbrInvoiceNumber === null) {
        setQrImage("");
        setImagesLoaded(true);
        return; // No FBR invoice number - skip QR generation
      }

      try {
        const qr = await QRCode.toDataURL(
          String(printData.fbrInvoiceNumber),
          {
            errorCorrectionLevel: 'M',
            margin: 1,
            width: 200
          }
        );
        setQrImage(qr);
        // Mark images as loaded after QR is generated
        setTimeout(() => setImagesLoaded(true), 200);
      } catch (err) {
        console.error("QR generation failed:", err);
        setImagesLoaded(true); // Still allow printing even if QR fails
      }
    };

    generateQR();
  }, [printData, isEstimate]);

  // Wait for images to load before marking as ready
  useEffect(() => {
    if (!isModalOpen || isEstimate) return;

    const checkImageLoad = () => {
      if (!compnentRef.current) return;

      const images = compnentRef.current.querySelectorAll('img');
      if (images.length === 0) {
        setImagesLoaded(true);
        return;
      }

      let loaded = 0;
      const total = images.length;

      images.forEach((img) => {
        if (img.complete && img.naturalHeight !== 0) {
          loaded++;
        } else {
          img.onload = () => {
            loaded++;
            if (loaded === total) setImagesLoaded(true);
          };
          img.onerror = () => {
            loaded++;
            if (loaded === total) setImagesLoaded(true);
          };
        }
      });

      if (loaded === total) setImagesLoaded(true);
    };

    // Check after a short delay to allow DOM to render
    const timer = setTimeout(checkImageLoad, 300);
    return () => clearTimeout(timer);
  }, [isModalOpen, isEstimate, qrImage]);

  // Auto print for regular invoices (with QR dependency only if FBR invoice exists)
  useEffect(() => {
    if (isEstimate) return; // Skip for estimates
    if (!isModalOpen) return;
    if (!location.state?.autoPrint && !autoPrint) return;
    if (!compnentRef.current) return;
    if (hasPrintedRef.current) return;
    if (isPrintingRef.current) return;

    // Only wait for QR if FBR invoice number exists
    const hasFbrInvoice = printData?.fbrInvoiceNumber &&
      printData.fbrInvoiceNumber !== "0" &&
      printData.fbrInvoiceNumber !== null;

    // Wait for QR image to be ready (only if FBR invoice exists)
    if (hasFbrInvoice && !qrImage) return;
    if (!imagesLoaded) return;

    safePrint();
  }, [isModalOpen, location.state?.autoPrint, autoPrint, qrImage, imagesLoaded, isEstimate, safePrint, printData?.fbrInvoiceNumber]);

  // Auto print for estimate invoices (no QR dependency)
  useEffect(() => {
    if (!isEstimate) return;
    if (!autoPrint) return;
    if (!isModalOpen) return;
    if (!compnentRef.current) return;
    if (hasPrintedRef.current) return;
    if (isPrintingRef.current) return;

    // For estimates, wait a bit for DOM to render
    const timer = setTimeout(() => {
      safePrint();
    }, 400);

    return () => clearTimeout(timer);
  }, [isModalOpen, isEstimate, autoPrint, safePrint]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (printTimeoutRef.current) {
        clearTimeout(printTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div>
      {user?.invoiceType !== "DIGITAL_INVOICING" ?
        <Modal
          title={isEstimate ? "Estimated Invoice" : "Print Invoice"}
          open={isModalOpen}
          footer={false}
          onCancel={() => setIsModalOpen(false)}
          width={300}
          zIndex={isEstimate ? 2000 : undefined}
          centered
        >
          <section
            ref={compnentRef}
            style={{
              borderRadius: 12,
              width: "100%",
              maxWidth: "260px",
              margin: "0 auto",
              padding: "6px",
              fontFamily: "Arial, Helvetica, sans-serif",
              fontSize: "12px",
              color: "#000",
              paddingTop: "10px",
              paddingBottom: "10px",
              WebkitFontSmoothing: "none",
              textRendering: "geometricPrecision",
            }}
          >
            {/* ================= COMPACT HEADER (FAZAL-PHARMACY STYLE) ================= */}
            <div style={{ marginBottom: "4px", textAlign: "center" }}>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  marginBottom: "2px",
                  textTransform: "uppercase",
                }}
              >
                {user.businessName}
              </div>
              {user?.address && (
                <div style={{ fontSize: "10px", fontWeight: 600 }}>{user.address}</div>
              )}
              <div style={{ fontSize: "10px", fontWeight: 600 }}>
                Ph: {user.businessPhone}
                {formatNtn(user.ntn) ? ` | NTN: ${formatNtn(user.ntn)}` : ""}
              </div>
              {licenceNumber && (
                <div style={{ fontSize: "10px", fontWeight: 600 }}>
                  Licence No: {licenceNumber}
                </div>
              )}
            </div>
            <div style={{ borderTop: "1.5px solid #000", margin: "4px 0" }} />

            <div
              style={{
                textAlign: "center",
                margin: "3px 0",
                fontWeight: "700",
                fontSize: "13px",
                letterSpacing: "0.5px",
              }}
            >
              Cash Memo
            </div>

            {isEstimate && (
              <>
                <div style={{ textAlign: "center", fontWeight: "700", margin: "3px 0" }}>
                  *** ESTIMATE ONLY ***
                </div>
                <div style={{ borderTop: "1.5px dashed #000", margin: "4px 0" }} />
              </>
            )}

            {/* ================= INVOICE INFO (TOP BAR) ================= */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "10px",
                fontWeight: 600,
                lineHeight: "1.4",
              }}
            >
              <div>
                <div>Memo#: {printData?.invoiceNumber}</div>
                <div>Payment: {printData?.paymentMode}</div>
              </div>
              <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <div>
                  Date:{" "}
                  {printData?.createdAt
                    ? (getPakistanDateTime(printData.createdAt).date || "-")
                    : "-"}
                </div>
                <div>
                  Time:{" "}
                  {printData?.createdAt
                    ? formatShortTime(getPakistanDateTime(printData.createdAt).time)
                    : "-"}
                </div>
              </div>
            </div>

            <div style={{ borderTop: "1.5px solid #000", margin: "4px 0" }} />


            {/* ================= CUSTOMER INFO ================= */}
            {(printData?.customerName || printData?.customerPhoneNumber) && (
              <>

                <div style={{ lineHeight: "1.4" }}>
                  <div>
                    Cust:&nbsp;
                    {printData?.customerName?.trim() && (
                      <span>{printData.customerName}</span>
                    )}
                    {printData?.customerPhoneNumber && (
                      <span>
                        {printData?.customerName?.trim() ? " | " : ""}
                        +92{printData.customerPhoneNumber}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ borderTop: "1.5px dashed #000", margin: "4px 0" }} />

              </>
            )}




            {/* ================= ITEMS (QTY / DESC / RATE / AMOUNT [ / DISC ]) ================= */}
            <div>
              <div
                style={{
                  display: "flex",
                  fontWeight: "700",
                  marginBottom: "2px",
                  borderBottom: "1.5px solid #000",
                  paddingBottom: "2px",
                }}
              >
                <span style={{ width: hasAnyItemDiscount ? "12%" : "15%", textAlign: "left" }}>Qty</span>
                <span style={{ width: hasAnyItemDiscount ? "32%" : "40%", textAlign: "left" }}>Description</span>
                <span style={{ width: hasAnyItemDiscount ? "18%" : "20%", textAlign: "right" }}>Rate</span>
                <span style={{ width: hasAnyItemDiscount ? "23%" : "25%", textAlign: "right" }}>Amount</span>
                {hasAnyItemDiscount && (
                  <span style={{ width: "15%", textAlign: "right" }}>Disc</span>
                )}
              </div>

              {printData?.cartItems?.map((item) => {
                const itemDiscountPct = Number(item.discount) || 0;
                const itemDiscountAmt = hasAnyItemDiscount ? getItemDiscountAmount(item) : 0;
                const showItemDisc = hasAnyItemDiscount && itemDiscountPct > 0;
                return (
                  <div
                    key={item._id}
                    style={{
                      display: "flex",
                      fontSize: "10px",
                      fontWeight: 600,
                      margin: "1px 0",
                    }}
                  >
                    <span style={{ width: hasAnyItemDiscount ? "12%" : "15%", textAlign: "left" }}>{item.quantity || 0}</span>
                    <span style={{ width: hasAnyItemDiscount ? "32%" : "40%", textAlign: "left", paddingRight: "2px" }}>
                      {item.title}
                    </span>
                    <span style={{ width: hasAnyItemDiscount ? "18%" : "20%", textAlign: "right" }}>
                      {safeToFixed(item.price)}
                    </span>
                    <span style={{ width: hasAnyItemDiscount ? "23%" : "25%", textAlign: "right" }}>
                      {safeToFixed((item.quantity || 0) * (item.price || 0))}
                    </span>
                    {hasAnyItemDiscount && (
                      <span style={{ width: "15%", textAlign: "right", fontSize: "9px" }}>
                        {showItemDisc ? `${Number(itemDiscountPct).toFixed(0)}% (−${safeToFixed(itemDiscountAmt)})` : ""}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ================= SUMMARY STRIP (TOTAL ITEMS / GROSS AMOUNT) ================= */}
            <div
              style={{
                borderTop: "1.5px solid #000",
                margin: "4px 0",
                paddingTop: "2px",
                fontSize: "10px",
                fontWeight: 600,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>
                Total Items:{" "}
                {Array.isArray(printData?.cartItems) ? printData.cartItems.length : 0}
              </span>
              <span>
                Gross Amount: {safeToFixed(
                  Array.isArray(printData?.cartItems)
                    ? printData.cartItems.reduce(
                      (sum, item) => sum + (item.quantity || 0) * (item.price || 0),
                      0
                    )
                    : printData?.subTotal ?? printData?.totalAmount ?? 0
                )}
              </span>
            </div>

            {/* ================= TAXES / POS FEE ================= */}
            <div
              style={{
                fontSize: "10px",
                fontWeight: 600,
                marginTop: "2px",
                lineHeight: "1.4",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Govt Tax</span>
                <span>{safeToFixed(printData?.tax)}</span>
              </div>
              {printData?.perHead != null && Number(printData.perHead) > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Per Head</span>
                  <span>{safeToFixed(printData.perHead)}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>POS Fee</span>
                <span>{safeToFixed(printData?.posFee != null ? printData.posFee : 1)}</span>
              </div>
              {(Number(printData?.furtherTax) || 0) > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Further Tax</span>
                  <span>{safeToFixed(printData.furtherTax)}</span>
                </div>
              )}
              {printData?.discount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>
                    Discount
                    {isPosInvoice
                      ? (posDiscountSummary.totalDiscountPercent > 0
                        ? ` (${Number(posDiscountSummary.totalDiscountPercent).toFixed(2)}%)`
                        : "")
                      : (() => {
                        const pct =
                          printData.discountPercent != null
                            ? printData.discountPercent
                            : (Number(printData?.subTotal) > 0
                              ? (Number(printData.discount) / Number(printData.subTotal)) * 100
                              : 0);
                        return pct > 0 ? ` (${Number(pct).toFixed(2)}%)` : "";
                      })()}
                  </span>
                  <span>-{safeToFixed(printData.discount)}</span>
                </div>
              )}
            </div>

            {/* ================= NET AMOUNT BOX ================= */}
            <div
              style={{
                border: "1.5px solid #000",
                padding: "4px 6px",
                marginTop: "4px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "12px",
                fontWeight: "700",
              }}
            >
              <span>Net Amount:</span>
              <span
                style={{
                  fontSize: "12px",
                  padding: "1px 6px",
                }}
              >
                {roundFigure(printData?.totalAmount)}
              </span>
            </div>

            {/* <div style={{ borderTop: "1px dashed #000", margin: "6px 0" }} /> */}

            {/* ================= FOOTER ================= */}
            <div style={{ textAlign: "center", marginTop: "6px" }}>
              {!isEstimate && printData?.fbrInvoiceNumber && printData.fbrInvoiceNumber !== "0" && printData.fbrInvoiceNumber !== null && (
                <>
                  <div className="flex justify-center items-center gap-[20px]">
                    <img
                      src={posType ? "/images/pra.jpeg" : "/images/fbr-logo.png"}
                      alt="logo"
                      style={{ width: "48px", height: "48px", objectFit: "cover" }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        console.warn("Logo image failed to load");
                      }}
                    />

                    {qrImage && (
                      <img
                        src={qrImage}
                        alt="QR"
                        style={{ width: "55px", height: "55px" }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          console.warn("QR code image failed to load");
                        }}
                      />
                    )}
                  </div>

                  <div style={{ fontSize: "10px", fontWeight: 600, marginTop: "2px" }}>
                    {posType ? "PRA Invoice #:" : "FBR Invoice #:"} {printData?.fbrInvoiceNumber}
                  </div>
                </>
              )}

              <div style={{ fontSize: "12px", marginTop: "4px", fontWeight: "700" }}>
                Thank you for your kind visit!
              </div>

              {/* ================= BRANDING ================= */}
              <div style={{ borderTop: "1.5px dashed #000", marginTop: "6px", paddingTop: "4px" }}>
                <div style={{ fontSize: "10px", fontWeight: 600, textAlign: "center", lineHeight: "1.4" }}>
                  <div>www.vareonofficial.com</div>
                  <div>+923316300886</div>
                </div>
              </div>
            </div>
          </section>

          <div className="flex justify-end mt-3">
            <Button type="primary" onClick={safePrint} disabled={isPrinting}>
              {isPrinting ? "Printing..." : "Print"}
            </Button>
          </div>
        </Modal> :
        <Modal
          open={isModalOpen}
          footer={false}
          onCancel={() => setIsModalOpen(false)}
          width={950}
          zIndex={isEstimate ? 2000 : undefined}
          centered
        >
          <div
            ref={compnentRef}
            style={{
              width: "210mm",
              minHeight: "297mm",
              padding: "16mm 18mm",
              background: "#fff",
              fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
              color: "#1a1a1a",
              fontSize: "11px",
              lineHeight: 1.5,
              letterSpacing: "0.01em",
              border: "1px solid #000",
              margin: "0 auto",
              boxSizing: "border-box",
              WebkitPrintColorAdjust: "exact",
              printColorAdjust: "exact",
            }}
          >
            {/* ================= HEADER ================= */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "20px",
              paddingBottom: "16px",
              borderBottom: "1.5px solid #000",
            }}>
              <div style={{ width: "58%", paddingRight: "12px" }}>
                <h1 style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#000",
                  letterSpacing: "0.02em",
                  lineHeight: 1.25,
                  marginBottom: "6px",
                }}>
                  {user.businessName}
                </h1>
                <h2 style={{
                  margin: 0,
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  lineHeight: 1.3,
                }}>
                  Sales Tax Invoice
                </h2>
                {isEstimate && (
                  <div style={{ marginTop: "10px", fontSize: "10px", fontWeight: 700, letterSpacing: "0.05em" }}>
                    *** ESTIMATE ONLY ***
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right", display: "flex", alignItems: "center", gap: "12px" }}>
                {!isEstimate && printData?.fbrInvoiceNumber && printData.fbrInvoiceNumber !== "0" && printData.fbrInvoiceNumber !== null && (
                  <>
                    <img
                      src="/images/fbrdigitalinvoicing.png"
                      alt="fbr"
                      style={{ width: "80px", height: "auto" }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        console.warn("FBR logo image failed to load");
                      }}
                    />
                    {qrImage && (
                      <img
                        src={qrImage}
                        alt="qr"
                        style={{ width: "64px", height: "64px" }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          console.warn("QR code image failed to load");
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ================= INVOICE META ================= */}
            <div style={{
              marginBottom: "20px",
              fontSize: "11px",
              fontWeight: 600,
              lineHeight: 1.6,
            }}>
              <div style={{ marginBottom: "4px" }}>
                <span style={{ fontWeight: 600 }}>Date: </span>
                <span>{printData?.invoiceDate ? new Date(printData.invoiceDate).toISOString().split("T")[0] : "—"}</span>
              </div>
              <div style={{ marginBottom: "4px" }}>
                <span style={{ fontWeight: 600 }}>Invoice No: </span>
                <span>{printData?.invoiceNumber}</span>
              </div>
              {printData?.fbrInvoiceNumber && printData.fbrInvoiceNumber !== "0" && printData.fbrInvoiceNumber !== null && (
                <div>
                  <span style={{ fontWeight: 600 }}>FBR Invoice No: </span>
                  <span>{printData.fbrInvoiceNumber}</span>
                </div>
              )}
            </div>

            {/* ================= SUPPLIER & BUYER ================= */}
            <div style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
              <div style={{
                flex: 1,
                border: "1px solid #000",
                padding: "14px 16px",
                lineHeight: 1.55,
              }}>
                <div style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  marginBottom: "10px",
                  paddingBottom: "6px",
                  borderBottom: "1px solid #000",
                }}>
                  Supplier
                </div>
                <div style={{ fontSize: "10.5px", fontWeight: 500, marginBottom: "5px" }}><span style={{ fontWeight: 600 }}>Name:</span> {user.businessName}</div>
                <div style={{ fontSize: "10.5px", fontWeight: 500, marginBottom: "5px" }}><span style={{ fontWeight: 600 }}>Address:</span> {user.address}</div>
                <div style={{ fontSize: "10.5px", fontWeight: 500, marginBottom: "5px" }}><span style={{ fontWeight: 600 }}>STRN:</span> {formatStrn(user.salesTaxNumber) || ""}</div>
                <div style={{ fontSize: "10.5px", fontWeight: 500 }}><span style={{ fontWeight: 600 }}>NTN:</span> {formatNtn(user.ntn) || ""}</div>
              </div>
              <div style={{
                flex: 1,
                border: "1px solid #000",
                padding: "14px 16px",
                lineHeight: 1.55,
              }}>
                <div style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  marginBottom: "10px",
                  paddingBottom: "6px",
                  borderBottom: "1px solid #000",
                }}>
                  Buyer
                </div>
                <div style={{ fontSize: "10.5px", fontWeight: 500, marginBottom: "5px" }}><span style={{ fontWeight: 600 }}>Name:</span> {printData?.customerName?.trim() || printData?.businessName?.trim() || "—"}</div>
                <div style={{ fontSize: "10.5px", fontWeight: 500, marginBottom: "5px" }}><span style={{ fontWeight: 600 }}>Address:</span> {printData?.customerAddress || "—"}</div>
                {/* <div style={{ fontSize: "10.5px", fontWeight: 500, marginBottom: "5px" }}><span style={{ fontWeight: 600 }}>STRN:</span> {formatStrn(printData?.salesTaxNumber) || "—"}</div> */}
                {printData?.salesTaxNumber &&
                  !/^0+$/.test(String(printData.salesTaxNumber).replace(/\D/g, "")) && (
                    <div
                      style={{
                        fontSize: "10.5px",
                        fontWeight: 500,
                        marginBottom: "5px",
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>STRN:</span>{" "}
                      {formatStrn(printData.salesTaxNumber)}
                    </div>
                  )}
                <div style={{ fontSize: "10.5px", fontWeight: 500 }}><span style={{ fontWeight: 600 }}>NTN:</span> {formatNtn(printData?.customerCnicOrNtn) || "—"}</div>
              </div>
            </div>

            {/* ================= TERMS ================= */}
            <table style={{
              width: "100%",
              marginBottom: "20px",
              borderCollapse: "collapse",
              fontSize: "10.5px",
              lineHeight: 1.5,
            }}>
              <tbody>
                <tr>
                  <td style={{ padding: "10px 12px", fontWeight: 600, width: "14%" }}>Terms of Sale</td>
                  <td style={{ padding: "10px 12px", fontWeight: 500, width: "20%" }}>{printData?.paymentMode || "—"}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 600, width: "18%" }}>Delivery Challan No</td>
                  <td style={{ padding: "10px 12px", fontWeight: 500, width: "18%" }}>{printData?.challanNumber || "—"}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 600, width: "12%" }}>Due Date</td>
                  <td style={{ padding: "10px 12px", fontWeight: 500 }}>
                    {printData?.dueDate ? new Date(printData.dueDate).toISOString().split("T")[0] : "—"}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ================= ITEMS TABLE ================= */}
            <table style={{
              width: "100%",
              marginBottom: "20px",
              borderCollapse: "collapse",
              fontSize: "10px",
              tableLayout: "fixed",
              lineHeight: 1.45,
            }}>
              <colgroup>
                <col style={{ width: "7%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <thead>
                <tr>
                  {["Qty", "Description", "UOM", "Rate", "Value Excl. Tax", "Sales Tax %", "Sales Tax", "Total"].map((h) => (
                    <th key={h} style={{
                      border: "1px solid #000",
                      padding: "10px 10px",
                      textAlign: h === "Description" ? "left" : "center",
                      fontWeight: 700,
                      fontSize: "10px",
                      letterSpacing: "0.02em",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {printData?.cartItems?.map((item, i) => (
                  <tr key={i}>
                    <td style={{ border: "1px solid #000", textAlign: "center", padding: "9px 10px", fontWeight: 500, verticalAlign: "middle" }}>{item.quantity || 0}</td>
                    <td style={{ border: "1px solid #000", padding: "9px 10px", fontWeight: 500, verticalAlign: "middle" }}>{item.title}</td>
                    <td style={{ border: "1px solid #000", textAlign: "center", padding: "9px 10px", fontWeight: 500, verticalAlign: "middle" }}>{item.uom || "—"}</td>
                    <td style={{ border: "1px solid #000", textAlign: "right", padding: "9px 10px", fontWeight: 500, verticalAlign: "middle" }}>{safeToFixed(item.price)}</td>
                    <td style={{ border: "1px solid #000", textAlign: "right", padding: "9px 10px", fontWeight: 500, verticalAlign: "middle" }}>
                      {safeToFixed((item.quantity || 0) * (item.price || 0))}
                    </td>
                    <td style={{ border: "1px solid #000", textAlign: "center", padding: "9px 10px", fontWeight: 500, verticalAlign: "middle" }}>
                      {formatTaxPercent(item)}
                    </td>
                    <td style={{ border: "1px solid #000", textAlign: "right", padding: "9px 10px", fontWeight: 500, verticalAlign: "middle" }}>
                      {safeToFixed(item.tax)}
                    </td>
                    <td style={{ border: "1px solid #000", textAlign: "right", padding: "9px 10px", fontWeight: 500, verticalAlign: "middle" }}>
                      {safeToFixed((item.quantity || 0) * (item.price || 0) + (item.tax || 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ================= TOTALS ================= */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "24px" }}>
              <table style={{ width: "280px", borderCollapse: "collapse", fontSize: "11px", lineHeight: 1.5 }}>
                <tbody>
                  <tr>
                    <td style={{ padding: "8px 14px", fontWeight: 600 }}>Total</td>
                    <td style={{ textAlign: "right", padding: "8px 14px", fontWeight: 500 }}>{safeToFixed(printData?.subTotal)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: "8px 14px", fontWeight: 600 }}>Sales Tax</td>
                    <td style={{ textAlign: "right", padding: "8px 14px", fontWeight: 500 }}>{safeToFixed(printData?.tax)}</td>
                  </tr>
                  {(Number(printData?.fedPayable) || 0) > 0 && (
                    <tr>
                      <td style={{ padding: "8px 14px", fontWeight: 600 }}>Fed Payable</td>
                      <td style={{ textAlign: "right", padding: "8px 14px", fontWeight: 500 }}>{safeToFixed(printData?.fedPayable)}</td>
                    </tr>
                  )}
                  {(Number(printData?.extraTax) || 0) > 0 && (
                    <tr>
                      <td style={{ padding: "8px 14px", fontWeight: 600 }}>Extra Tax</td>
                      <td style={{ textAlign: "right", padding: "8px 14px", fontWeight: 500 }}>{safeToFixed(printData?.extraTax)}</td>
                    </tr>
                  )}
                  {(Number(printData?.furtherTax) || 0) > 0 && (
                    <tr>
                      <td style={{ padding: "8px 14px", fontWeight: 600 }}>Further Tax</td>
                      <td style={{ textAlign: "right", padding: "8px 14px", fontWeight: 500 }}>{safeToFixed(printData?.furtherTax)}</td>
                    </tr>
                  )}
                  {(Number(printData?.discount) || 0) > 0 && (
                    <tr>
                      <td style={{ padding: "8px 14px", fontWeight: 600 }}>
                        Discount
                        {printData?.discountPercent != null && Number(printData.discountPercent) > 0
                          ? ` (${Number(printData.discountPercent).toFixed(2)}%)`
                          : (Number(printData?.subTotal) > 0
                            ? ` (${((Number(printData.discount) / Number(printData.subTotal)) * 100).toFixed(2)}%)`
                            : "")}
                      </td>
                      <td style={{ textAlign: "right", padding: "8px 14px", fontWeight: 500, color: "#16a34a" }}>
                        -{safeToFixed(printData?.discount)}
                      </td>
                    </tr>
                  )}
                  <tr style={{ borderTop: "1.5px solid #000" }}>
                    <td style={{ padding: "12px 14px", fontWeight: 700, fontSize: "12px" }}>Net Value</td>
                    <td style={{ textAlign: "right", padding: "12px 14px", fontWeight: 700, fontSize: "12px" }}>
                      {safeToFixed(printData?.totalAmount)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ================= SIGNATURE ================= */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              marginBottom: "24px",
              paddingTop: "20px",
              fontSize: "11px",
            }}>
              <div style={{ fontWeight: 600, letterSpacing: "0.02em" }}>Signatures</div>
              <div style={{ textAlign: "center" }}>
                <div style={{
                  border: "1px solid #000",
                  height: "52px",
                  width: "140px",
                  marginBottom: "8px",
                }} />
                <div style={{ fontWeight: 600, fontSize: "10px", letterSpacing: "0.03em" }}>Company Seal</div>
              </div>
            </div>

            {/* ================= FOOTER BRANDING ================= */}
            <div style={{
              borderTop: "1px solid #000",
              paddingTop: "12px",
              textAlign: "center",
              fontSize: "10px",
              fontWeight: 600,
              lineHeight: 1.5,
              letterSpacing: "0.02em",
              color: "#333",
            }}>
              <div>www.vareonofficial.com</div>
              <div>+923316300886</div>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button type="primary" onClick={safePrint} disabled={isPrinting}>
              {isPrinting ? "Printing..." : "Print"}
            </Button>
          </div>
        </Modal>

      }

    </div>
  );
};

export default PrintInvoice;
